import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const ThreadReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const threadMeasurements = measurements.filter((measurement) => measurement.reportType === 'THREAD');
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  useEffect(() => {
    if (!threadMeasurements.length) {
      setActiveThreadId(null);
      return;
    }

    if (!activeThreadId || !threadMeasurements.some((measurement) => measurement.id === activeThreadId)) {
      setActiveThreadId(threadMeasurements[0].id);
    }
  }, [activeThreadId, threadMeasurements]);
  const activeThread = threadMeasurements.find((measurement) => measurement.id === activeThreadId) || threadMeasurements[0];
  const effectiveFrequency = metadata?.frequency || frequency || 'per pipe end';
  const taperMin = getProjectLimitMin(projectLimitFields, 'thread_taper_mm_m', 61);
  const taperMax = getProjectLimitMax(projectLimitFields, 'thread_taper_mm_m', 64);
  const leadMax = getProjectLimitMax(projectLimitFields, 'thread_lead_mm_cum', 0.25);
  const heightMin = getProjectLimitMin(projectLimitFields, 'thread_height_mm', 1.75);
  const heightMax = getProjectLimitMax(projectLimitFields, 'thread_height_mm', 1.85);
  const odGaugeMin = getProjectLimitMin(projectLimitFields, 'thread_od_gauge_mm', 659.8);
  const odGaugeMax = getProjectLimitMax(projectLimitFields, 'thread_od_gauge_mm', 661);
  const taperLimitText = getProjectLimitText(projectLimitFields, 'thread_taper_mm_m', `${taperMin} - ${taperMax} mm/m`);
  const leadLimitText = getProjectLimitText(projectLimitFields, 'thread_lead_mm_cum', `<= ${leadMax} mm cum`);
  const heightLimitText = getProjectLimitText(projectLimitFields, 'thread_height_mm', `${heightMin} - ${heightMax} mm`);
  const odGaugeLimitText = getProjectLimitText(projectLimitFields, 'thread_od_gauge_mm', 'API 5MT tol');
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['Taper', taperLimitText, true],
    ['Lead', leadLimitText, true],
    ['Height', heightLimitText, true],
    ['OD@gauge', odGaugeLimitText],
  ]);
  const getThreadNumber = (value?: string) => {
    const parsed = Number(value);
    return value !== undefined && value !== '' && Number.isFinite(parsed) ? parsed : Number.NaN;
  };
  const setup = {
    threadType: activeThread?.threadType || '-',
    standard: activeThread?.threadStandard || '-',
    gaugeSet: activeThread?.threadGaugeSetRef || '-',
    endTested: activeThread?.threadEndTested || '-',
    couplingApplied: activeThread?.threadCouplingApplied || 'no',
    visualDamage: activeThread?.threadVisualDamage || 'no',
  };
  const gaugeRows = [
    { field: 'taper', label: 'taper', value: getThreadNumber(activeThread?.threadTaperMmPerM), unit: 'mm/m', min: Math.max(0, taperMin * 0.8), max: Math.max(taperMax * 1.2, getThreadNumber(activeThread?.threadTaperMmPerM) || 0), lo: taperMin, hi: taperMax, formula: taperLimitText },
    { field: 'lead', label: 'lead', value: getThreadNumber(activeThread?.threadLeadMmCum), unit: 'mm cum', min: 0, max: Math.max(leadMax * 1.2, getThreadNumber(activeThread?.threadLeadMmCum) || 0), hi: leadMax, formula: leadLimitText },
    { field: 'height', label: 'height', value: getThreadNumber(activeThread?.threadHeightMm), unit: 'mm', min: Math.max(0, heightMin * 0.8), max: Math.max(heightMax * 1.2, getThreadNumber(activeThread?.threadHeightMm) || 0), lo: heightMin, hi: heightMax, formula: heightLimitText },
    { field: 'pipe_end_diameter', label: 'pipe end diameter', value: getThreadNumber(activeThread?.threadPipeEndDiameterMm), unit: 'mm', min: Math.max(0, odGaugeMin * 0.8), max: Math.max(odGaugeMax * 1.2, getThreadNumber(activeThread?.threadPipeEndDiameterMm) || 0), lo: odGaugeMin, hi: odGaugeMax, formula: odGaugeLimitText },
  ];
  const rowStatus = (row: typeof gaugeRows[number]) => {
    if (!activeThread || !Number.isFinite(row.value)) return 'PENDING_REVIEW';
    if (row.lo != null && row.value < row.lo) return 'FAIL';
    if (row.hi != null && row.value > row.hi) return 'FAIL';
    return setup.visualDamage === 'yes' ? 'FAIL' : 'PASS';
  };
  const overallStatus = !activeThread ? 'PENDING_REVIEW' : gaugeRows.some((row) => rowStatus(row) === 'FAIL') || setup.visualDamage === 'yes' ? 'FAIL' : gaugeRows.some((row) => rowStatus(row) === 'PENDING_REVIEW') ? 'PENDING_REVIEW' : 'PASS';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Thread gauge results</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">{activeThread ? `Selected ${setup.gaugeSet}` : 'No thread gauge result saved yet'}</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('THREAD')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Gauge Result
          </Button>
        </div>

        <div>
          <SectionHead title="Threaded pipe profile" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="p-4">
              <svg viewBox="0 0 480 120" className="h-[130px] w-full">
                <rect x="0" y="46" width="200" height="28" fill="#EEF0F3" stroke="#7A8490" />
                <path d="M 200 46 L 380 32 L 380 88 L 200 74 Z" fill="#DCEEF8" stroke="#5A86A7" />
                {Array.from({ length: 18 }).map((_, index) => {
                  const x = 204 + index * 9.5;
                  return <line key={index} x1={x} y1={32 + index * 0.7} x2={x} y2={88 - index * 0.7} stroke="#5A86A7" strokeWidth="0.7" />;
                })}
                <text x="400" y="60" fontFamily="monospace" fontSize="10" fill="#4B5563">pipe end (gauge plane)</text>
                <text x="100" y="40" fontFamily="monospace" fontSize="10" fill="#4B5563" textAnchor="middle">pipe body</text>
                <text x="290" y="105" fontFamily="monospace" fontSize="10" fill="#4B5563" textAnchor="middle">{setup.threadType} - taper {Number.isFinite(gaugeRows[0].value) ? `${gaugeRows[0].value} mm/m` : '-'}</text>
              </svg>
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Gauge measurements" />
            {gaugeRows.map((row) => (
              <TensileResultRow
                key={row.field}
                label={row.label}
                formula={row.formula}
                value={row.value}
                unit={row.unit}
                min={row.min}
                max={row.max}
                lo={row.lo}
                hi={row.hi}
                status={rowStatus(row)}
              />
            ))}
          </CardContent>
        </Card>

        <div>
          <SectionHead title="Connection setup" />
          <Card className="border-[#E2E5EA]">
            <CardContent className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-6">
              {[
                ['Thread type', setup.threadType],
                ['Standard', setup.standard],
                ['Gauge set', setup.gaugeSet],
                ['End tested', setup.endTested],
                ['Coupling applied', setup.couplingApplied === 'yes' ? 'Yes' : 'No'],
                ['Visual damage', setup.visualDamage === 'yes' ? 'Yes' : 'None'],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className="mt-2 font-mono text-[12px] font-semibold text-blueprint-blue">{value}</div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Saved gauge results" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Gauge set</th>
                  <th className="px-4 py-3 text-right">Taper</th>
                  <th className="px-4 py-3 text-right">Lead</th>
                  <th className="px-4 py-3 text-right">Height</th>
                  <th className="px-4 py-3 text-right">OD gauge</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {threadMeasurements.map((measurement) => (
                  <tr key={measurement.id} onClick={() => setActiveThreadId(measurement.id)} className={cn('cursor-pointer hover:bg-page-bg', activeThread?.id === measurement.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{measurement.threadGaugeSetRef || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.threadTaperMmPerM || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.threadLeadMmCum || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.threadHeightMm || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">{measurement.threadPipeEndDiameterMm || '-'}</td>
                    <td className="px-4 py-3"><StatusBadge status={measurement.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <ItemActionButtons measurement={measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows rows={[['Frequency', effectiveFrequency, true], ['Results', `${threadMeasurements.length} saved`, true], ['Status', <StatusBadge key="status" status={overallStatus} />]]} />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
